import { useRef, useState, type ChangeEvent } from "react";
import { useTranslation } from "react-i18next";
import { ExternalLink, Link2, Paperclip, Trash2 } from "lucide-react";
import { toast } from "sonner";
import type { CanHolder } from "../../lib/access";
import { toApiFailure } from "../../lib/errors";
import { notifyApiError, type ErrorMessages } from "../../lib/toast-error";
import { formatDateTime } from "../../lib/format";
import { userName, type UserSummary } from "../users/api";
import { Button } from "../../components/ui/button";
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
} from "../../components/ui/card";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "../../components/ui/dialog";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import { Textarea } from "../../components/ui/textarea";
import {
  downloadAttachment,
  useAttachmentPreview,
  useAttachments,
  useCreateAttachmentLink,
  useRemoveAttachment,
  useUploadAttachments,
  type AttachmentResponse,
} from "./api";
import {
  ACCEPT_ATTRIBUTE,
  ATTACHMENT_PROBLEM_KEY,
  formatBytes,
  isPreviewableImage,
  problemFilename,
  validateFiles,
} from "./files";
import { canAddAttachment, canRemoveAttachment } from "./permissions";

/** F-017: the removal Begruendung must carry at least 10 characters. */
const MIN_REMOVAL_REASON_CHARS = 10;

/** Client-side URL shape check for link attachments (server re-validates). */
const LINK_URL_PATTERN = /^https?:\/\/\S+$/i;

type NotifyError = (error: unknown) => void;

/**
 * Error notifier that first maps the attachment-specific RFC-7807 problem
 * titles (error.attachment_*) to SPA catalog messages - prefixed with the
 * failing filename when the problem's field errors name one - then falls back
 * to the shared status-code handling (mirrors the postmortem notifier).
 */
function useAttachmentNotifier(): NotifyError {
  const { t } = useTranslation();
  const errorMessages: ErrorMessages = {
    precondition: t("errors.precondition"),
    forbidden: t("errors.forbidden"),
    conflict: t("errors.conflict"),
    generic: t("errors.generic"),
  };
  return (error: unknown): void => {
    const failure = toApiFailure(error);
    const mappedKey = failure.problem
      ? ATTACHMENT_PROBLEM_KEY[failure.problem.title]
      : undefined;
    if (mappedKey) {
      const filename = problemFilename(failure.problem?.errors);
      const message = t(mappedKey);
      toast.error(filename ? `${filename}: ${message}` : message);
      return;
    }
    notifyApiError(error, errorMessages);
  };
}

/**
 * "Anhaenge & Links" card on the incident detail screen (M-005: attachments
 * have no separate screen). Lists every slot including removed ones (F-017
 * keeps the metadata row with a removal notice), and offers upload, link-add
 * and removal where the incident's `can` holds `attachment.add` and
 * `attachment.remove` (F-006, F-017).
 */
export function AttachmentsCard({
  incidentId,
  incident,
  userById,
}: {
  incidentId: string;
  /** The incident's `can` decides adding and removing ([F-027]). */
  incident: CanHolder;
  userById: Map<string, UserSummary>;
}): JSX.Element {
  const { t } = useTranslation();
  const notifyError = useAttachmentNotifier();

  const attachmentsQuery = useAttachments(incidentId);
  const upload = useUploadAttachments(incidentId);
  const createLink = useCreateAttachmentLink(incidentId);
  const removeAttachment = useRemoveAttachment(incidentId);

  const fileInputRef = useRef<HTMLInputElement>(null);
  const [linkUrl, setLinkUrl] = useState("");
  const [linkDescription, setLinkDescription] = useState("");
  const [removeTarget, setRemoveTarget] = useState<AttachmentResponse | null>(
    null,
  );
  const [removeReason, setRemoveReason] = useState("");

  const mayAdd = canAddAttachment(incident);
  const mayRemove = canRemoveAttachment(incident);

  const resetFileInput = (): void => {
    if (fileInputRef.current) fileInputRef.current.value = "";
  };

  const onFilesSelected = (event: ChangeEvent<HTMLInputElement>): void => {
    const files = Array.from(event.target.files ?? []);
    if (files.length === 0) return;

    // Early client-side budget check (F-006); the server re-validates.
    const validation = validateFiles(files);
    if (validation.tooMany) {
      toast.error(t("attachments.tooMany"));
      resetFileInput();
      return;
    }
    if (validation.tooLarge.length > 0) {
      toast.error(
        t("attachments.tooLarge", { files: validation.tooLarge.join(", ") }),
      );
      resetFileInput();
      return;
    }

    upload.mutate(files, {
      onSuccess: () => toast.success(t("attachments.uploaded")),
      onError: (error) => notifyError(error),
      onSettled: () => resetFileInput(),
    });
  };

  const onAddLink = (): void => {
    const url = linkUrl.trim();
    if (!LINK_URL_PATTERN.test(url)) {
      toast.error(t("attachments.linkUrlInvalid"));
      return;
    }
    const description = linkDescription.trim();
    createLink.mutate(
      { url, ...(description ? { description } : {}) },
      {
        onSuccess: () => {
          setLinkUrl("");
          setLinkDescription("");
          toast.success(t("attachments.linkAdded"));
        },
        onError: (error) => notifyError(error),
      },
    );
  };

  const closeRemoveDialog = (): void => {
    setRemoveTarget(null);
    setRemoveReason("");
  };

  const onConfirmRemove = (): void => {
    if (!removeTarget) return;
    const reason = removeReason.trim();
    if (reason.length < MIN_REMOVAL_REASON_CHARS) return;
    removeAttachment.mutate(
      { attachmentId: removeTarget.id, reason },
      {
        onSuccess: () => {
          closeRemoveDialog();
          toast.success(t("attachments.removed"));
        },
        onError: (error) => {
          closeRemoveDialog();
          notifyError(error);
        },
      },
    );
  };

  const onDownload = (attachment: AttachmentResponse): void => {
    downloadAttachment(attachment).catch((error: unknown) =>
      notifyError(error),
    );
  };

  const items = attachmentsQuery.data ?? [];
  const reasonChars = removeReason.trim().length;

  return (
    <Card data-testid="incident-detail_attachments">
      <CardHeader>
        <CardTitle className="text-sm font-medium text-muted-foreground">
          {t("attachments.title")}
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-4">
        {attachmentsQuery.isLoading ? (
          <div className="space-y-2">
            <div className="h-10 w-full animate-pulse rounded-md bg-muted" />
            <div className="h-10 w-full animate-pulse rounded-md bg-muted" />
          </div>
        ) : attachmentsQuery.isError ? (
          <p
            data-testid="incident-detail_attachments-error"
            className="text-sm text-muted-foreground"
          >
            {t("errors.load")}
          </p>
        ) : items.length === 0 ? (
          <p
            data-testid="incident-detail_attachments-empty"
            className="text-sm text-muted-foreground"
          >
            {t("attachments.empty")}
          </p>
        ) : (
          <ul data-testid="incident-detail_attachment-list" className="space-y-2">
            {items.map((attachment) => (
              <AttachmentRow
                key={attachment.id}
                attachment={attachment}
                userById={userById}
                mayRemove={mayRemove}
                onDownload={onDownload}
                onRemove={(target) => setRemoveTarget(target)}
              />
            ))}
          </ul>
        )}

        {mayAdd ? (
          <div className="space-y-4 border-t pt-4">
            <div className="space-y-1.5">
              <Label htmlFor="attachment-files">{t("attachments.upload")}</Label>
              <Input
                id="attachment-files"
                data-testid="incident-detail_attachment-upload"
                ref={fileInputRef}
                type="file"
                multiple
                accept={ACCEPT_ATTRIBUTE}
                onChange={onFilesSelected}
                disabled={upload.isPending}
              />
              <p className="text-xs text-muted-foreground">
                {upload.isPending
                  ? t("attachments.uploading")
                  : t("attachments.uploadHint")}
              </p>
            </div>

            <div className="space-y-1.5">
              <Label htmlFor="attachment-link-url">
                {t("attachments.addLink")}
              </Label>
              <div className="flex flex-col gap-2 sm:flex-row">
                <Input
                  id="attachment-link-url"
                  data-testid="incident-detail_attachment-link-url"
                  value={linkUrl}
                  onChange={(event) => setLinkUrl(event.target.value)}
                  placeholder={t("attachments.linkUrlPlaceholder")}
                  aria-label={t("attachments.linkUrl")}
                />
                <Input
                  data-testid="incident-detail_attachment-link-description"
                  value={linkDescription}
                  onChange={(event) => setLinkDescription(event.target.value)}
                  placeholder={t("attachments.linkDescription")}
                  aria-label={t("attachments.linkDescription")}
                />
                <Button
                  size="sm"
                  data-testid="incident-detail_attachment-link-add"
                  className="sm:self-center"
                  onClick={onAddLink}
                  disabled={createLink.isPending || linkUrl.trim().length === 0}
                >
                  {createLink.isPending
                    ? t("attachments.linkAdding")
                    : t("attachments.linkSubmit")}
                </Button>
              </div>
            </div>
          </div>
        ) : null}
      </CardContent>

      <Dialog
        open={removeTarget !== null}
        onOpenChange={(open) => {
          if (!open) closeRemoveDialog();
        }}
      >
        <DialogContent data-testid="incident-detail_attachment-remove-dialog">
          <DialogHeader>
            <DialogTitle>{t("attachments.removeTitle")}</DialogTitle>
            <DialogDescription>
              {removeTarget
                ? t("attachments.removeIntro", {
                    name: removeTarget.filenameOrUrl,
                  })
                : null}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-1.5">
            <Label htmlFor="attachment-remove-reason">
              {t("attachments.removeReason")}
            </Label>
            <Textarea
              id="attachment-remove-reason"
              data-testid="incident-detail_attachment-remove-reason"
              rows={3}
              value={removeReason}
              onChange={(event) => setRemoveReason(event.target.value)}
            />
            <p className="text-xs text-muted-foreground">
              {t("attachments.removeReasonHint", { chars: reasonChars })}
            </p>
          </div>
          <DialogFooter>
            <Button
              variant="outline"
              data-testid="incident-detail_attachment-remove-cancel"
              onClick={closeRemoveDialog}
            >
              {t("common.cancel")}
            </Button>
            <Button
              variant="destructive"
              data-testid="incident-detail_attachment-remove-confirm"
              onClick={onConfirmRemove}
              disabled={
                removeAttachment.isPending ||
                reasonChars < MIN_REMOVAL_REASON_CHARS
              }
            >
              {removeAttachment.isPending
                ? t("attachments.removing")
                : t("attachments.removeConfirm")}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </Card>
  );
}

/**
 * One attachment slot. Removed slots (F-017) keep the row but lose the
 * download affordance and carry the removal notice; live files download via
 * the authenticated api client, live links open externally.
 */
function AttachmentRow({
  attachment,
  userById,
  mayRemove,
  onDownload,
  onRemove,
}: {
  attachment: AttachmentResponse;
  userById: Map<string, UserSummary>;
  mayRemove: boolean;
  onDownload: (attachment: AttachmentResponse) => void;
  onRemove: (attachment: AttachmentResponse) => void;
}): JSX.Element {
  const { t } = useTranslation();
  const removed = attachment.removedAt !== null;
  const isLink = attachment.kind === "link";
  const Icon = isLink ? Link2 : Paperclip;

  return (
    <li
      data-testid={`incident-detail_attachment-row_${attachment.id}`}
      className="rounded-md border p-3"
    >
      <div className="flex flex-wrap items-start justify-between gap-2">
        <div className="flex min-w-0 items-start gap-2">
          <Icon className="mt-0.5 h-4 w-4 shrink-0 text-muted-foreground" />
          <div className="min-w-0 space-y-0.5">
            {removed ? (
              <span
                data-testid={`incident-detail_attachment-removed-name_${attachment.id}`}
                className="break-all text-sm text-muted-foreground line-through"
              >
                {attachment.filenameOrUrl}
              </span>
            ) : isLink ? (
              <a
                href={attachment.filenameOrUrl}
                target="_blank"
                rel="noopener noreferrer"
                data-testid={`incident-detail_attachment-open_${attachment.id}`}
                className="inline-flex items-center gap-1 break-all text-sm font-medium underline underline-offset-4 hover:text-foreground"
              >
                {attachment.filenameOrUrl}
                <ExternalLink className="h-3.5 w-3.5 shrink-0" />
              </a>
            ) : (
              <button
                type="button"
                data-testid={`incident-detail_attachment-download_${attachment.id}`}
                onClick={() => onDownload(attachment)}
                title={t("attachments.download")}
                className="break-all text-left text-sm font-medium underline underline-offset-4 hover:text-foreground"
              >
                {attachment.filenameOrUrl}
              </button>
            )}
            {!isLink && attachment.sizeBytes !== null ? (
              <p className="text-xs text-muted-foreground">
                {formatBytes(attachment.sizeBytes)}
              </p>
            ) : null}
            {isLink && attachment.description ? (
              <p className="text-xs text-muted-foreground">
                {attachment.description}
              </p>
            ) : null}
            <p className="text-xs text-muted-foreground">
              {t("attachments.uploadedBy")}{" "}
              {userName(attachment.uploadedById, userById)} -{" "}
              {formatDateTime(attachment.uploadedAt)}
            </p>
            {removed ? (
              <p
                data-testid={`incident-detail_attachment-removed-notice_${attachment.id}`}
                className="text-xs text-muted-foreground"
              >
                {attachment.removedById
                  ? t("attachments.removedNotice", {
                      name: userName(attachment.removedById, userById),
                      date: formatDateTime(attachment.removedAt),
                      reason: attachment.removalReason ?? "-",
                    })
                  : // GR-006: the retention sweep removed it - no person, but
                    // the marker "System-Aufbewahrungsfrist" and the date.
                    t("attachments.removedNoticeRetention", {
                      date: formatDateTime(attachment.removedAt),
                      reason: attachment.removalReason ?? "-",
                    })}
              </p>
            ) : null}
          </div>
        </div>
        {mayRemove && !removed ? (
          <Button
            variant="ghost"
            size="sm"
            data-testid={`incident-detail_attachment-remove_${attachment.id}`}
            onClick={() => onRemove(attachment)}
            className="text-destructive-text hover:text-destructive-text"
          >
            <Trash2 className="h-4 w-4" />
            {t("attachments.remove")}
          </Button>
        ) : null}
      </div>
      {isPreviewableImage(attachment) ? (
        <AttachmentImagePreview attachment={attachment} onOpen={onDownload} />
      ) : null}
    </li>
  );
}

/**
 * [F-006]/AC-070: the image itself, so the reader can check what was attached
 * without downloading it. It stays at the slot for as long as the content
 * exists; a removed slot has no preview. Clicking it downloads the original.
 */
function AttachmentImagePreview({
  attachment,
  onOpen,
}: {
  attachment: AttachmentResponse;
  onOpen: (attachment: AttachmentResponse) => void;
}): JSX.Element | null {
  const { t } = useTranslation();
  const src = useAttachmentPreview(attachment.id, true);
  if (!src) return null;
  return (
    <button
      type="button"
      data-testid={`incident-detail_attachment-preview_${attachment.id}`}
      onClick={() => onOpen(attachment)}
      title={t("attachments.download")}
      className="mt-2 block max-w-full overflow-hidden rounded-md border bg-muted/40"
    >
      <img
        src={src}
        alt={t("attachments.previewAlt", { name: attachment.filenameOrUrl })}
        className="max-h-48 max-w-full object-contain"
      />
    </button>
  );
}
